<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="apikey-modal-overlay"
      role="presentation"
    >
      <div
        class="apikey-modal-dock"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apikey-modal-title"
        @click.stop
      >
        <div class="apikey-modal">
          <button
            type="button"
            class="apikey-modal__close"
            :aria-label="t('apiKey.close')"
            @click="close"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="apikey-modal__head">
            <div
              id="apikey-modal-title"
              class="apikey-modal__title"
              role="heading"
              aria-level="2"
            >
              {{ t('apiKey.title') }}
            </div>
            <p class="apikey-modal__desc">
              {{ hasUin ? t('apiKey.desc') : t('apiKey.loginHint') }}
            </p>
          </div>

          <div class="apikey-modal__body">
            <!-- 已登录：展示 key（打码）+ 右侧复制 -->
            <template v-if="hasUin">
              <div class="apikey-modal__row">
                <code
                  class="apikey-modal__key"
                  :class="{ 'apikey-modal__key--muted': state !== 'ready' }"
                >{{ keyDisplay }}</code>
                <button
                  type="button"
                  class="apikey-modal__copy"
                  :disabled="state !== 'ready'"
                  @click="onCopy"
                >
                  <Copy :size="16" :stroke-width="2" />
                  {{ copied ? t('apiKey.copied') : t('apiKey.copy') }}
                </button>
              </div>
              <button
                v-if="state === 'error'"
                type="button"
                class="apikey-modal__retry"
                @click="load"
              >
                {{ t('apiKey.retry') }}
              </button>
            </template>

            <!-- 未登录：引导登录 -->
            <div v-else class="apikey-modal__row">
              <code class="apikey-modal__key apikey-modal__key--muted">{{ t('apiKey.masked') }}</code>
              <button type="button" class="apikey-modal__copy" @click="onLoginToCopy">
                {{ t('apiKey.loginToCopy') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Copy } from 'lucide-vue-next'
import { closeToast, showToast } from 'vant'
import { useApiKeyModal } from '@/composables/useApiKeyModal'
import { useAuthModal } from '@/composables/useAuthModal'
import { useSessionAuth } from '@/composables/useSessionAuth'
import { apiKeyErrorI18nKey, fetchApiKey, maskApiKey } from '@/services/apiKeyApi'
import { t } from '@/composables/useLandingLocale'
import { track } from '@/adapters/analytics'
import { ANALYTICS_CLICK_POINT, ANALYTICS_EVENTS, ANALYTICS_PAGE } from '@/constants/analytics'

const { visible, open, close } = useApiKeyModal()
const { open: openAuthModal } = useAuthModal()
const { hasUin, uin } = useSessionAuth()

type State = 'idle' | 'loading' | 'ready' | 'error'
const state = ref<State>('idle')
/** 完整明文 key，仅在组件内存持有：不写 store/localStorage/URL/日志/埋点。 */
const fullKey = ref('')
const errorText = ref('')
/** 未登录时点「登录后复制」：先关本弹窗去登录，登录成功后再自动弹回。 */
const pendingReopen = ref(false)
const copied = ref(false)
let copiedResetTimer: ReturnType<typeof setTimeout> | null = null

const COPY_FEEDBACK_MS = 2000

function clearCopiedFeedback() {
  if (copiedResetTimer != null) {
    clearTimeout(copiedResetTimer)
    copiedResetTimer = null
  }
  copied.value = false
}

function markCopied() {
  copied.value = true
  if (copiedResetTimer != null) clearTimeout(copiedResetTimer)
  copiedResetTimer = setTimeout(() => {
    copied.value = false
    copiedResetTimer = null
  }, COPY_FEEDBACK_MS)
}

const keyDisplay = computed(() => {
  if (state.value === 'loading') return t('apiKey.loading')
  if (state.value === 'error') return errorText.value
  if (state.value === 'ready') return maskApiKey(fullKey.value)
  return t('apiKey.masked')
})

async function load() {
  if (!hasUin.value) return
  state.value = 'loading'
  try {
    const res = await fetchApiKey()
    fullKey.value = res.api_key
    state.value = 'ready'
    track(ANALYTICS_EVENTS.apiKeyExpose, {
      page_name: ANALYTICS_PAGE.landing,
      component_name: 'apiKey_modal',
      has_key: true,
      ...(uin.value !== undefined ? { uin: uin.value } : {}),
    })
    // 自动复制：失败不 toast（避免与后续「已复制」共用实例导致文案闪变）
    await copyKey({ silentFail: true })
  } catch (err) {
    errorText.value = t(apiKeyErrorI18nKey(err))
    state.value = 'error'
    track(ANALYTICS_EVENTS.apiKeyExpose, {
      page_name: ANALYTICS_PAGE.landing,
      component_name: 'apiKey_modal',
      has_key: false,
      ...(uin.value !== undefined ? { uin: uin.value } : {}),
    })
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable')
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    try {
      ta.select()
      return document.execCommand('copy')
    } catch {
      return false
    } finally {
      document.body.removeChild(ta)
    }
  }
}

function showCopiedToast() {
  // 文案固定为「已复制」；先关掉旧 toast，避免 Vant 原地改 message 造成闪变
  const message = t('apiKey.copied')
  closeToast()
  showToast({
    message,
    type: 'text',
    wordBreak: 'normal',
  })
}

async function copyKey(opts?: { silentFail?: boolean }) {
  if (state.value !== 'ready') return
  const ok = await copyText(fullKey.value)
  if (ok) {
    markCopied()
    showCopiedToast()
  } else if (!opts?.silentFail) {
    showToast({
      message: t('apiKey.copyFail'),
      type: 'text',
      wordBreak: 'normal',
    })
  }
  track(ANALYTICS_EVENTS.apiKeyClick, {
    page_name: ANALYTICS_PAGE.landing,
    component_name: 'apiKey_modal',
    click_point: ANALYTICS_CLICK_POINT.copy,
    has_key: ok,
    ...(uin.value !== undefined ? { uin: uin.value } : {}),
  })
}

function onCopy() {
  void copyKey()
}

function onLoginToCopy() {
  // 隐藏当前弹窗 → 展示登录弹窗；登录成功后由 watch(hasUin) 自动弹回并拉取
  pendingReopen.value = true
  close()
  openAuthModal()
  track(ANALYTICS_EVENTS.apiKeyClick, {
    page_name: ANALYTICS_PAGE.landing,
    component_name: 'apiKey_modal',
    click_point: ANALYTICS_CLICK_POINT.copy,
    has_key: false,
    ...(uin.value !== undefined ? { uin: uin.value } : {}),
  })
}

// 打开弹窗：已登录拉取，未登录仅展示引导；关闭清空明文与复制反馈
watch(visible, (v) => {
  if (!v) {
    fullKey.value = ''
    state.value = 'idle'
    clearCopiedFeedback()
    return
  }
  if (hasUin.value) {
    load()
  } else {
    // 弹窗曝光：未登录无 key，直接上报
    track(ANALYTICS_EVENTS.apiKeyExpose, {
      page_name: ANALYTICS_PAGE.landing,
      component_name: 'apiKey_modal',
      has_key: false,
      ...(uin.value !== undefined ? { uin: uin.value } : {}),
    })
  }
})

// 登录成功（notifySessionAuthChanged → hasUin 翻转）后：
// 若来自「登录后复制」则弹回本弹窗（由 watch(visible) 触发拉取）；否则弹窗仍开着则直接拉取
watch(hasUin, (v) => {
  if (!v) return
  if (pendingReopen.value) {
    pendingReopen.value = false
    open()
    return
  }
  if (visible.value && state.value !== 'ready') load()
})
</script>

<style scoped>
.apikey-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;
}

.apikey-modal-dock {
  position: relative;
  flex-shrink: 0;
  width: 440px;
  max-width: 100%;
  border-radius: 20px;
}

.apikey-modal {
  --accent: #ff6907;
  --ink: #181818;
  --muted: #666;
  --muted2: #9c9c9c;
  --line: rgba(0, 0, 0, 0.06);
  --shell: #f7f7f7;

  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  overflow-x: hidden;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  line-height: normal;
  color: var(--ink);
  border: 1px solid #fff;
  border-radius: 20px;
  background:
    radial-gradient(circle at 36% -4%, #ffdbc3 0, rgba(255, 219, 195, 0.58) 10%, rgba(255, 255, 255, 0) 28%),
    #fff;
}

.apikey-modal__close {
  position: absolute;
  top: 9px;
  right: 11px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  color: #666;
  background: transparent;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;
}

.apikey-modal__close:hover {
  color: var(--ink);
  background: var(--line);
}

.apikey-modal__head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 32px;
}

.apikey-modal__title {
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  line-height: normal;
}

.apikey-modal__desc {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: normal;
}

.apikey-modal__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px;
}

.apikey-modal__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 8px 8px 8px 16px;
  background: var(--shell);
  border-radius: 10px;
  overflow: hidden;
}

.apikey-modal__key {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  color: var(--ink);
}

.apikey-modal__key--muted {
  color: var(--muted);
}

.apikey-modal__copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.apikey-modal__copy:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.apikey-modal__retry {
  align-self: flex-start;
  padding: 6px 12px;
  color: var(--accent);
  font-size: 13px;
  background: transparent;
  border: 1px solid var(--accent);
  border-radius: 8px;
  cursor: pointer;
}
</style>
